import { useEffect } from "react";

/** How close to the left edge an opening swipe has to start. */
const EDGE_ZONE_PX = 200;

/** How far a swipe has to travel horizontally before it counts. */
const SWIPE_DISTANCE_PX = 60;

/** Past this much vertical drift the gesture is a scroll, not a swipe. */
const VERTICAL_TOLERANCE_PX = 45;

/**
 * Opens an off-canvas drawer on a left-to-right swipe from the screen edge and
 * closes it again on a right-to-left one.
 *
 * The gesture is measured between `touchstart` and `touchend` and never calls
 * `preventDefault`, so it cannot block scrolling. Opening swipes additionally
 * have to start within `EDGE_ZONE_PX` of the left edge, which keeps them clear
 * of the horizontally scrollable data tables.
 *
 * @param {object} params
 * @param {boolean} params.isOpen - current drawer state
 * @param {Function} params.onOpen
 * @param {Function} params.onClose
 * @param {Function} [params.isEnabled] - evaluated per gesture, so a viewport
 *   change does not need to re-render the caller
 */
export function useDrawerSwipe({ isOpen, onOpen, onClose, isEnabled }) {
  useEffect(() => {
    let startX = null;
    let startY = null;

    const handleTouchStart = (event) => {
      startX = null;
      // pinch and other multi-touch gestures are not swipes
      if (event.touches.length !== 1) {
        return;
      }
      const touch = event.touches[0];
      // A closing swipe may start anywhere: the backdrop covers the page, so
      // the only thing under the finger is the drawer itself.
      if (!isOpen && touch.clientX > EDGE_ZONE_PX) {
        return;
      }
      startX = touch.clientX;
      startY = touch.clientY;
    };

    const handleTouchEnd = (event) => {
      if (startX === null) {
        return;
      }
      const touch = event.changedTouches[0];
      const deltaX = touch.clientX - startX;
      const deltaY = Math.abs(touch.clientY - startY);
      startX = null;

      if (deltaY > VERTICAL_TOLERANCE_PX) {
        return;
      }
      if (isEnabled && !isEnabled()) {
        return;
      }
      if (!isOpen && deltaX >= SWIPE_DISTANCE_PX) {
        onOpen();
      } else if (isOpen && deltaX <= -SWIPE_DISTANCE_PX) {
        onClose();
      }
    };

    // an interrupted gesture is not a swipe, however far it happened to travel
    const handleTouchCancel = () => {
      startX = null;
    };

    // passive: the handlers only measure, so the browser need not wait on them
    const options = { passive: true };
    document.addEventListener("touchstart", handleTouchStart, options);
    document.addEventListener("touchend", handleTouchEnd, options);
    document.addEventListener("touchcancel", handleTouchCancel, options);

    return () => {
      document.removeEventListener("touchstart", handleTouchStart, options);
      document.removeEventListener("touchend", handleTouchEnd, options);
      document.removeEventListener("touchcancel", handleTouchCancel, options);
    };
  }, [isOpen, onOpen, onClose, isEnabled]);
}
